/**
 * A modal dialog.
 *
 * Modality is a promise to the user: while this is open, nothing behind it can
 * be reached. Keeping that promise means the focus ring cannot leave the
 * dialog, Escape always closes it, and whatever opened it gets focus back — so
 * a keyboard user is returned to exactly where they were.
 */

import { useCallback, useEffect, useId, useRef } from 'react'
import { createPortal } from 'react-dom'
import type { KeyboardEvent, MouseEvent, ReactElement, ReactNode, RefObject } from 'react'

import { IconButton } from './Button'
import styles from './Modal.module.css'

function cx(...values: Array<string | false | null | undefined>): string {
  return values.filter((value): value is string => typeof value === 'string' && value !== '').join(' ')
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'button:not([disabled])',
  'iframe',
  'object',
  'embed',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(',')

function focusableWithin(root: HTMLElement): HTMLElement[] {
  // Deliberately not a layout test: measuring boxes here would make the trap
  // depend on the element being painted, which is false in a test renderer and
  // during the frame in which the dialog first appears.
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) => !element.hasAttribute('hidden') && element.getAttribute('aria-hidden') !== 'true',
  )
}

function CloseIcon(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
      <path
        d="m4 4 8 8M12 4l-8 8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  )
}

export type ModalSize = 'sm' | 'md' | 'lg'

export interface ModalProps {
  open: boolean
  onClose: () => void
  /** Names the dialog. Always rendered — a dialog without a name is unusable. */
  title: string
  /** One line under the title, and the dialog's accessible description. */
  description?: ReactNode
  children?: ReactNode
  /** Buttons for the bottom bar. Put the primary action last. */
  footer?: ReactNode
  size?: ModalSize
  /** Focused on open instead of the first focusable element. */
  initialFocusRef?: RefObject<HTMLElement>
  /** Off for dialogs holding unsaved work, where a stray click should not discard it. */
  closeOnOverlayClick?: boolean
  /** Temporarily disable the close control while an external picker is open. */
  closeDisabled?: boolean
  /**
   * `stage` is the meeting surface: one dark ground, in either appearance.
   * The ordinary dialog chrome — a light card and a tinted footer — leaves
   * video sitting on the wrong background.
   */
  tone?: 'default' | 'stage'
  className?: string
}

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  initialFocusRef,
  closeOnOverlayClick = true,
  closeDisabled = false,
  tone = 'default',
  className,
}: ModalProps): ReactElement | null {
  const baseId = useId()
  const titleId = `${baseId}-title`
  const descriptionId = `${baseId}-description`
  const dialogRef = useRef<HTMLDivElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return undefined

    const restoreTo = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const dialog = dialogRef.current
    const body = bodyRef.current

    // The first control in the body, not the close button that happens to come
    // first in the DOM. With nothing to focus, the dialog itself takes it, so
    // its name is announced and Tab starts from the top.
    const target =
      initialFocusRef?.current ?? (body === null ? null : (focusableWithin(body)[0] ?? null)) ?? dialog
    target?.focus()

    // The page behind must not scroll away under the dialog.
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = previousOverflow
      restoreTo?.focus()
    }
  }, [open, initialFocusRef])

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>): void => {
      if (event.key === 'Escape') {
        // Stopped here so a nested dialog closes only itself.
        event.stopPropagation()
        onClose()
        return
      }

      if (event.key !== 'Tab') return

      const dialog = dialogRef.current
      if (dialog === null) return

      const items = focusableWithin(dialog)
      const first = items[0]
      const last = items[items.length - 1]

      if (first === undefined || last === undefined) {
        // Nothing to move to, so Tab must not escape to the page behind.
        event.preventDefault()
        dialog.focus()
        return
      }

      const active = document.activeElement

      if (event.shiftKey && (active === first || active === dialog)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    },
    [onClose],
  )

  // Closing on mouse-down would also fire when a selection drag that started
  // inside the dialog happens to end on the overlay, which loses the user's work.
  const onOverlayClick = useCallback(
    (event: MouseEvent<HTMLDivElement>): void => {
      if (!closeOnOverlayClick) return
      if (event.target !== overlayRef.current) return
      onClose()
    },
    [closeOnOverlayClick, onClose],
  )

  if (!open) return null

  const hasDescription = description !== undefined && description !== null && description !== ''

  return createPortal(
    <div
      ref={overlayRef}
      className={styles.overlay}
      onClick={onOverlayClick}
      onKeyDown={onKeyDown}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={hasDescription ? descriptionId : undefined}
        tabIndex={-1}
        data-tone={tone === 'stage' ? 'stage' : undefined}
        className={cx(styles.dialog, styles[size], tone === 'stage' && styles.stage, className)}
      >
        <header className={styles.header}>
          <div className={styles.heading}>
            <h2 className={styles.title} id={titleId}>
              {title}
            </h2>
            {hasDescription ? (
              <p className={styles.description} id={descriptionId}>
                {description}
              </p>
            ) : null}
          </div>
          <IconButton label="Close" size="sm" disabled={closeDisabled} onClick={onClose} className={styles.close}>
            <CloseIcon />
          </IconButton>
        </header>

        {children !== undefined && children !== null ? (
          <div className={styles.body} ref={bodyRef}>
            {children}
          </div>
        ) : null}

        {footer !== undefined && footer !== null ? (
          <footer className={styles.footer}>{footer}</footer>
        ) : null}
      </div>
    </div>,
    document.body,
  )
}
